import { ProductLayout } from './add-edit-product/add-edit-product-layout/add-edit-product-layout.model';
import { MarketRatePlan, ContractTerms } from './add-edit-product/product-pricing/rate-plan.model';
import { CodeMatch } from './add-edit-product/advanced-settings/code-matching/code-match.model';

export class Product {

    public id: number;
    public systemId: string;
    public name: string;
    public displayTypeId: ProductDisplayType; // wizardId
    public configurationTypeId: ProductConfigurationType;
    public orderItemType: OrderItemType;
    public autoRenewalIndicator: ProductAutoRenewalIndicator;
    public productTypeId: number;
    public productVersion: string;
    public catalogCode: string;
    public unitOfMeasure: string;
    public description: string;
    public categories: Array<ProductCategory>;
    public createdDate: string;
    public modifiedDate: string;
    public createdBy: string;
    public modifiedBy: string;
    public image: string;
    public alternativeText: string;
    public versionNumber : number;

    // Pricing
    public displayTypePricingId: number;
    public pricingCode: number; // visible only if Pricing Mechanism == "Pricebook Lookup" (displayTypePricingId == '0' )
    public price: number;
    public priceBuilder: string;
    public costBuilder: string;
    public baseRecurringPrice: number;
    public recurringPriceFormula: string;
    public recurringPricePeriodId : number;
    public recurringPricePeriodFormula: string;
    public recurringCostFormula: string;
    public recurringCostPeriodId : number;
    public recurringCostPeriodFormula: string;

    // Permissions and Effectivity dates

    public active: boolean;
    public visibleToEveryone: boolean;
    public permissionSelected: Array<number>;
    public permissionStartDate: string;
    public permissionEndDate: string;
    public permissionStartDateTimestamp: string;
    public permissionEndDateTimestamp: string;

    // Additional Product Data
    public externalId: string;
    public upc: string;
    public mpn: string;
    public inventory: number;
    public leadTime: string;
    public familyCode: string;
    public weight: string;
    public imageLarge: string;
    public descriptionLong: string;
    public executeRulesOnce: number;
    public allowConfigurationImport: number;
    public script: string;
    public configurationTemplate: string;
    public updateAsset: number;
    public alwaysAllowRenewal: number;
    public endStatus: number;
    public replacementProductSystemId: string;
    public replacementProductName: string;
    public isSyncedFromBackOffice: boolean;
    public sapEffectiveDate: string;
    public subscriptionBillingInfo : SubscriptionBillingInfo;
    public isRootItem : boolean;
    public generalItemCategoryGroup: string;
    public hasUsagePricing? : boolean;
    public isBundleProduct? : boolean;
    public divisionId: number;
    public oldMaterialNumber: string;
    public productHierarchy: string;
    public crossPlantMaterialStatus: CrossPlantMaterialStatus;
    public crossPlantMaterialStatusValidFrom: string;
    public xDistributionChainStatus: XDistributionChannelStatus;
    public xDistributionChainStatusValidFrom: string;

    // Advanced Formulas
    public descriptionBuilder: string;
    public userCanEnterQuantity: string;
    public skipRelatedProductsLoading: boolean;

    // Obsolete
    public defaultItemType: string;
    public userCanChangeItemType: string;
    public value3: string;

    // Shipping Costs
    public shippings: Array<ShippingCost>;

    public layout: ProductLayout;

    public codeMatching: Array<CodeMatch>;

    public customFields: Array<ProductCustomField>;

    constructor() {
        this.baseRecurringPrice = 0;
        this.price = 0;
        this.permissionSelected = [];
        this.active = true;
        this.subscriptionBillingInfo = new SubscriptionBillingInfo();
        this.customFields = [];
    }
}

export class SubscriptionBillingInfo {
    public contractTerms: ContractTerms;
    public marketRatePlans : Array<MarketRatePlan>;

    constructor() {
        this.contractTerms = new ContractTerms();
        this.marketRatePlans = new Array<MarketRatePlan>();
    }
}

export class ProductCustomField {
    public id: number;
    public systemId: string;
    public name: string;
    public label: string;
    public type: string;
    public rank: number;
    public isSalesAreaRelevant: boolean;
    public content: string;
}

export class ProductCategory {
    public id: number;
    public name: string;
    public rank: number;
    public isDisabled?: boolean;
}

export class ShippingCost {
    public id: number;
    public name: string;
    public expression: string;

    constructor(id: number = 0, name: string = '', expression: string = '')
    {
        this.id = id;
        this.name = name;
        this.expression = expression;
    }
}

export class ProductAlias {
    public id: number;
    public alias: Product;
    public productId: number;
    public productName: string;

    constructor() {
        this.alias = new Product();
    }
}

export enum ProductDisplayType {
    Simple = 0,
    Configurable = 1,
    Collection = 2, // collection of configurable products
    System = 3, // system list of configurable prod
    GuidedSelling = 4, // Guided selling ili atribute based search
    ParentChild = 5 // ParentChild
}

export enum OrderItemType {
    Sales = 0,
    Subscription = 1,
    SubscriptionBilling = 2,
    Service = 3,
    ServiceContract = 4
}

export enum ProductAutoRenewalIndicator {
    NoSelectionCanBeChanged = 0,
    SetToYesSelectionCanBeChanged = 1,
    NotRelevantForAutoRenewal = 2,
    SetToYesSelectionCanNotBeChanged = 3
}

export enum XDistributionChannelStatus {
    Ok = 0,
    Warning = 1,
    Error = 2
}

export enum CrossPlantMaterialStatus {
    Ok = 0,
    Warning = 1,
    Error = 2
}

export enum ProductConfigurationType {
    Standard = 0,
    Variant = 1, 
    External = 2 
}

export enum ProductPricingType {
    PricebookLookups = 0,
    CustomPricing = 1,
    SubscriptionPricing = 2,
    VariantPricing = 3
}

export enum RecurringPeriodType {
    Formula = 0,
    Daily = 1,
    Monthly = 2,
    Yearly = 3,
    None = 4
}

export enum ExportType {
    ExportProducts = 1,
    ExportProductsAndRefine = 2,
    ExportProductTranslations = 3,
    ExportProductTranslationsAndRefine = 4
}

export enum HelpLink {
  Attributes = "https://help.sap.com/viewer/884885f05e6b4c8082254d4d9d63f19b/LATEST/en-US/9d8df1a2aa374eba8d2a666809414966.html",
  Products = "https://help.sap.com/docs/SAP_CPQ/884885f05e6b4c8082254d4d9d63f19b/753cb4233271443585f86fa5f07f0055.html?locale=en-US",
  Categories = "https://help.sap.com/docs/SAP_CPQ/884885f05e6b4c8082254d4d9d63f19b/e2ff672414694fce99195d67257a77dd.html?locale=en-US"
}